<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ProgressSteps from "@/components/website/ProgressSteps.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import {computed, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";

const store = useStore();

const emit = defineEmits(['next', 'back']);
const industry = ref('');
const country = ref('');
const region = ref('');
const location = ref('');

const regions = ref({});
const locations = ref({});
const loading = computed(() => store.getters['singleWebsite/loading']);
const errors = computed(() => store.getters['singleWebsite/errors']);

defineProps({
  countries: {
    required: true,
    type: Object,
  },
  industries: {
    required: true,
    type: Object,
  },
});

onMounted(async () => {
  let country = store.getters['singleWebsite/country'];
  if (country !== '') {
    region.value = store.getters['singleWebsite/region'];
    regions.value = await store.dispatch('singleWebsite/fetchLocations', country);
  }

  let regionGetter = store.getters['singleWebsite/region'];
  if (regionGetter !== '') {
    location.value = store.getters['singleWebsite/location'];
    locations.value = await store.dispatch('singleWebsite/fetchLocations', regionGetter);
  }
});

watch(country, async (newValue) => {
  if (newValue !== '' && country.value !== store.getters['singleWebsite/country']) {
    region.value = '';
    regions.value = await store.dispatch('singleWebsite/fetchLocations', newValue);

    clearErrors('country');
  }

  if (newValue === '') {
    region.value = '';
    regions.value = {};
  }
});

watch(region, async (newValue) => {
  if (newValue !== '' && country.value !== store.getters['singleWebsite/region']) {
    location.value = '';
    locations.value = await store.dispatch('singleWebsite/fetchLocations', newValue);
  }

  if (newValue === '') {
    location.value = '';
    locations.value = {};
  }
});

watch(industry, (newValue) => {
  if (newValue !== '') {
    clearErrors('industry');
  }
});

const clearErrors = field => {
  const filteredErrors = Object.fromEntries(
    Object.entries(errors.value).filter(([key]) => key !== field)
  );

  store.dispatch('singleWebsite/setErrors', {errors: filteredErrors});
}

const handleContinue = async () => {
  await store.dispatch('singleWebsite/setLoading', true);

  const errors = {};

  if (!industry.value) {
    errors.industry = {message: 'Please fill the field industry'};
  }

  if (!country.value) {
    errors.country = {message: 'Please fill the field country'};
  }

  if (Object.keys(errors).length > 0) {
    await store.dispatch('singleWebsite/setErrors', {errors});
    await store.dispatch('singleWebsite/setLoading', false);

    return;
  }

  saveToState();

  await store.dispatch('singleWebsite/create');
};


const handleBack = () => {
  saveToState();

  store.dispatch('singleWebsite/setErrors', {});

  emit('back');
}

const saveToState = () => {
  store.dispatch('singleWebsite/setStepTwo', {
    industry: industry.value,
    country: country.value,
    region: region.value,
    location: location.value,
  });
}

industry.value = store.getters['singleWebsite/industry'];
country.value = store.getters['singleWebsite/country'];
region.value = store.getters['singleWebsite/region'];
location.value = store.getters['singleWebsite/location'];
</script>

<template>
  <div
    class="tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-text-center tw-max-w-default-container tw-mx-auto tw-py-6 tw-min-h-[680px]"
  >
    <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6 tw-gap-8 2xl:tw-w-2/5 tw-w-3/4">
      <div class="tw-flex tw-flex-col">
        <h1 class="tw-text-4xl tw-font-bold">
          Website Information
        </h1>
      </div>

      <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
        <label
          for="industry"
          class="tw-self-start tw-text-lg tw-text-grey-dark"
        >
          Your website's industry:
        </label>
        <CustomMultiselect
          id="industry"
          v-model="industry"
          class="tw-text-left"
          :close-on-select="true"
          :options="industries"
          :searchable="true"
          :validation-errors="errors.industry"
        />
      </div>

      <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
        <label
          for="country"
          class="tw-self-start tw-text-lg tw-text-grey-dark"
        >
          Location of business:
        </label>
        <CustomMultiselect
          id="country"
          v-model="country"
          class="tw-text-left"
          :close-on-select="true"
          :options="countries"
          :searchable="true"
          :validation-errors="errors.country"
        />
      </div>

      <div
        v-if="Object.keys(regions).length > 0"
        class="tw-flex tw-flex-col tw-w-full tw-gap-2"
      >
        <label
          for="region"
          class="tw-self-start tw-text-lg tw-text-grey-dark"
        >
          Region of business <span class="tw-italic">(Optional)</span>:
        </label>
        <CustomMultiselect
          id="region"
          v-model="region"
          class="tw-text-left"
          :close-on-select="true"
          :options="regions"
          :searchable="true"
          groups
        />
      </div>

      <div
        v-if="Object.keys(locations).length > 0"
        class="tw-flex tw-flex-col tw-w-full tw-gap-2"
      >
        <label
          for="location"
          class="tw-self-start tw-text-lg tw-text-grey-dark"
        >
          Location of business <span class="tw-italic">(Optional)</span>:
        </label>
        <CustomMultiselect
          id="location"
          v-model="location"
          class="tw-text-left"
          :close-on-select="true"
          :options="locations"
          :searchable="true"
          groups
        />
      </div>

      <div class="tw-flex tw-w-full tw-justify-between">
        <DefaultButton
          variant="base"
          :disabled="loading"
          @click="handleBack"
        >
          Back
        </DefaultButton>

        <DefaultButton
          :disabled="loading"
          @click="handleContinue"
        >
          Add Website
        </DefaultButton>
      </div>

      <div class="tw-flex tw-flex-col">
        <ProgressSteps
          :current-step="2"
          :hide-step-three="true"
        />
      </div>
    </div>
  </div>
</template>
